// lib/demo/preset-data.ts
// Demo 站预置数据：首次访问（IndexedDB 无任何 plan）时自动注入示例数据
// 让新用户立刻看到"有内容"的产品状态，而非空荡荡的空白页
//
// 设计（乔布斯视角）：
//   第一印象决定留存。新用户打开看到一个空列表 = "这产品没东西" → 流失。
//   注入一份完整的前端学习计划 + 几张复习卡 + 两天学习日志，
//   用户立刻能体验"知识树→面试题→复习→打卡"完整闭环，理解产品价值。
//   用户创建自己的第一个真实计划时，温和提示清除示例数据。
//
// 幂等性：用固定 ID（demo-frontend-plan / demo-card-N / demo-log-N），
//   重复调用 injectDemoData 只会覆盖不会叠加。

import { getItem, listItems, delItem, bulkPutItems } from "@/lib/storage/db";
import { deletePlanSummary, toSummary } from "@/lib/plan-summary";
import { invalidateCache } from "@/lib/storage/cache";
import { createCard } from "@/lib/fsrs";
import { KEY_PREFIXES } from "@/lib/types";
import type { LearningPlan, ReviewCard, LearnLog } from "@/lib/types";
import { chinaDateNow, chinaDateShift } from "@/lib/time";
import { loadPresetData } from "@/lib/presets";

/** Demo 计划固定 ID（幂等：重复注入只覆盖不叠加） */
const DEMO_PLAN_ID = "demo-frontend-plan";

/**
 * localStorage flag：标记是否已注入过 demo 数据（每个浏览器一次性）
 * 避免用户手动删除 demo plan 后再次进入首页又重新注入
 */
const DEMO_INJECTED_FLAG = "demo:injected";

/**
 * 检测是否需要注入 Demo 数据
 * 规则：① 未注入过（localStorage flag 不存在）+ ② IndexedDB 中没有任何 plan
 * 注入一次后设置 flag，即使用户删除 demo plan 也不会重新注入
 */
export async function shouldInjectDemo(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  // 已注入过 → 不再注入（避免用户删了 demo plan 后又重新出现）
  try {
    if (window.localStorage.getItem(DEMO_INJECTED_FLAG) === "1") return false;
  } catch {
    // localStorage 不可用，继续走下面的检查
  }
  // 直接检查 demo plan 是否已存在（快速路径）
  const existing = await getItem<LearningPlan>(KEY_PREFIXES.PLAN + DEMO_PLAN_ID);
  if (existing) return false;
  // 检查是否有任意 plan（用户可能已创建自己的计划）
  const plans = await listItems<LearningPlan>(KEY_PREFIXES.PLAN);
  return plans.length === 0;
}

/**
 * 注入 Demo 数据：
 * 1. frontend preset 计划（标记 isDemo: true）
 * 2. 3 张 FSRS 卡片（关联该计划的 nodeId）
 * 3. 2 天 LearnLog（前 1 天和前 2 天，type=learn_complete）
 */
export async function injectDemoData(): Promise<void> {
  if (typeof window === "undefined") return;

  const preset = await loadPresetData("frontend");
  if (!preset) {
    console.warn("[injectDemoData] loadPresetData(frontend) 返回空，跳过注入");
    return;
  }

  const now = new Date().toISOString();
  const today = chinaDateNow();

  // 1. Demo 计划（基于 frontend preset）
  const plan: LearningPlan = {
    id: DEMO_PLAN_ID,
    topic: preset.topic,
    knowledgeTree: preset.knowledgeTree,
    questions: preset.questions,
    schedule: preset.schedule,
    dailyMinutes: 30,
    maxNewPerDay: 1,
    fsrsMode: "standard",
    isDemo: true,
    createdAt: now,
    updatedAt: now,
  };

  // 2. 3 张 FSRS 卡片（取前 3 道面试题）
  const demoQuestions = preset.questions.slice(0, 3);
  const cards: ReviewCard[] = demoQuestions.map((q, i) => {
    const card = createCard(plan.id, q.nodeId, q.id, q.question, q.answer, "standard");
    card.id = `demo-card-${i + 1}`;
    return card;
  });

  // 3. 2 天 LearnLog
  const logs: LearnLog[] = [
    {
      id: "demo-log-1",
      planId: plan.id,
      nodeId: demoQuestions[0]?.nodeId,
      questionId: demoQuestions[0]?.id,
      date: chinaDateShift(today, -2),
      type: "learn_complete",
    },
    {
      id: "demo-log-2",
      planId: plan.id,
      nodeId: demoQuestions[1]?.nodeId,
      questionId: demoQuestions[1]?.id,
      date: chinaDateShift(today, -1),
      type: "learn_complete",
    },
  ];

  // 2026-07-31 优化（卡帕西视角）：
  //   旧版 7 次串行 setItem（plan + summary + 3 cards + 2 logs）= 7 个独立 IndexedDB 事务，
  //   每个事务有 RTT 开销 + 事务冲突风险。若用户在此期间导航到其他页面并发起写入，
  //   可能触发 "Data lost due to missing file" 事务中止错误。
  //   改为 bulkPutItems 单事务批量写入（1 个事务完成全部 7 条记录），
  //   写入耗时从 ~50ms 降到 ~10ms，且消除并发冲突窗口。
  const summary = toSummary(plan);
  // bulkPutItems<unknown>：混合类型写入（plan/summary/card/log），统一用 unknown 避免类型冲突
  await bulkPutItems<unknown>([
    { key: KEY_PREFIXES.PLAN + plan.id, value: plan },
    { key: KEY_PREFIXES.PLAN_SUMMARY + plan.id, value: summary },
    ...cards.map((c) => ({ key: KEY_PREFIXES.CARD + c.id, value: c })),
    ...logs.map((l) => ({ key: KEY_PREFIXES.LEARN_LOG + l.id, value: l })),
  ]);
  // 失效 summary 列表缓存（bulkPutItems 逐条失效 key 缓存，但列表级缓存需手动失效）
  invalidateCache("__cache:plan_summaries");

  // 标记已注入（一次性，避免用户删除 demo plan 后重新注入）
  try {
    window.localStorage.setItem(DEMO_INJECTED_FLAG, "1");
  } catch {
    // localStorage 不可用，静默忽略
  }
}

/**
 * 清除所有 Demo 数据：
 * - 删除所有 isDemo=true 的计划 + 对应 summary
 * - 删除关联这些计划的卡片 + 孤儿 demo 卡片（id 以 demo-card- 开头）
 * - 删除关联这些计划的 LearnLog + 孤儿 demo 日志（id 以 demo-log- 开头）
 *
 * 孤儿数据兜底：用户可能在 /learn/list 手动删过 demo plan（早期 deletePlan 不联动清理），
 * 导致 demo plan 不存在但 demo cards 残留。此处用 id 前缀兜底清理。
 */
export async function clearDemoData(): Promise<void> {
  if (typeof window === "undefined") return;

  // 1. 找出所有 demo 计划
  const plans = await listItems<LearningPlan>(KEY_PREFIXES.PLAN);
  const demoPlans = plans.filter((p) => p.isDemo === true);
  const demoPlanIds = new Set(demoPlans.map((p) => p.id));

  // 2. 删除 demo 计划 + summary
  for (const plan of demoPlans) {
    await delItem(KEY_PREFIXES.PLAN + plan.id);
    await deletePlanSummary(plan.id);
  }

  // 3. 删除关联 demo 计划的卡片 + 孤儿 demo 卡片（id 前缀兜底）
  const cards = await listItems<ReviewCard>(KEY_PREFIXES.CARD);
  for (const card of cards) {
    if (demoPlanIds.has(card.planId) || card.id.startsWith("demo-card-")) {
      await delItem(KEY_PREFIXES.CARD + card.id);
    }
  }

  // 4. 删除关联 demo 计划的 LearnLog + 孤儿 demo 日志（id 前缀兜底）
  const logs = await listItems<LearnLog>(KEY_PREFIXES.LEARN_LOG);
  for (const log of logs) {
    if (demoPlanIds.has(log.planId) || log.id.startsWith("demo-log-")) {
      await delItem(KEY_PREFIXES.LEARN_LOG + log.id);
    }
  }
}

/**
 * 检测是否存在 Demo 数据（用于创建真实计划后提示清除）
 * 检查范围：demo plan（isDemo）+ 孤儿 demo 卡片（id 前缀）+ 孤儿 demo 日志（id 前缀）
 * 孤儿兜底：demo plan 可能已被手动删除，但 cards/logs 残留
 */
export async function hasDemoData(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const plans = await listItems<LearningPlan>(KEY_PREFIXES.PLAN);
  if (plans.some((p) => p.isDemo === true)) return true;
  // 孤儿 demo cards（plan 已删但卡残留）
  const cards = await listItems<ReviewCard>(KEY_PREFIXES.CARD);
  if (cards.some((c) => c.id.startsWith("demo-card-"))) return true;
  // 孤儿 demo logs
  const logs = await listItems<LearnLog>(KEY_PREFIXES.LEARN_LOG);
  if (logs.some((l) => l.id.startsWith("demo-log-"))) return true;
  return false;
}
